import type { InsuranceRequirements } from '../types';

interface CoiUploadData {
  name: string;
  description: string;
  accessType: string;
  pageExpiration: boolean;
  expirationDate: string;
  effectiveDate: string;
  endDate: string;
  certificateHolderName?: string;
  certificateHolderAddress?: {
    line1: string;
    line2: string;
    line3: string;
    locality: string;
    region: string;
    postalcode: string;
  };
  insuranceRequirements: {
    edges: Array<{
      node: {
        coverageRules: {
          edges: Array<{
            node: {
              ruleName: string;
              ruleJson: string;
            };
          }>;
        };
      };
    }>;
  };
  additionalInsureds: {
    edges: Array<{
      node: {
        name: string;
        address: {
          line1: string;
          line2: string;
          line3: string;
          locality: string;
          region: string;
          postalcode: string;
        };
      };
    }>;
  };
  organization: {
    name: string;
    certificateHolderName?: string;
    certificateHolderAddress?: {
      line1: string;
      line2: string;
      line3: string;
      locality: string;
      region: string;
      postalcode: string;
    };
  };
}

interface TransformedData {
  company: {
    name: string;
    subtitle: string;
  };
  header: {
    title: string;
    description: string;
  };
  insurance: InsuranceRequirements;
  additionalInsureds: {
    title: string;
    items: Array<{
      name: string;
      address: string;
      isCertificateHolder?: boolean;
    }>;
  };
  certificateRequirements: {
    title: string;
    items: string[];
  };
}

const formatAddress = (address: {
  line1: string;
  line2: string;
  line3: string;
  locality: string;
  region: string;
  postalcode: string;
}): string => {
  return `${address.line1}${
    address.line2 ? `, ${address.line2}` : ''
  }${address.line3 ? `, ${address.line3}` : ''}${
    address.locality ? `, ${address.locality}` : ''
  }${address.region ? `, ${address.region}` : ''}${
    address.postalcode ? ` ${address.postalcode}` : ''
  }`;
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const parseRuleJson = (ruleJson: string) => {
  try {
    return JSON.parse(ruleJson);
  } catch {
    return null;
  }
};

export const transformCoiData = (data: CoiUploadData): TransformedData => {
  // Transform insurance requirements
  const insurance: InsuranceRequirements = {
    generalLiability: {
      title: 'General Liability:',
      items: [],
    },
    autoLiability: {
      title: 'Auto Liability:',
      amount: '',
      description: '',
    },
    workersCompensation: {
      title: "Workers' Compensation:",
      description: '',
      items: [],
    },
    professionalLiability: {
      title: 'Professional Liability:',
      amount: '',
      description: '',
    },
    excessUmbrellaLiability: {
      title: 'Umbrella Liability:',
      items: [],
    },
    excessLiability: {
      title: 'Excess Liability:',
      items: [],
    },
    cyberLiability: {
      title: 'Cyber Liability:',
      items: [],
    },
    crimeLiability: {
      title: 'Crime Liability:',
      items: [],
    },
    automobileLiability: {
      title: 'Automobile Liability:',
      items: [],
    },
    certificateRequirements: {
      title: 'Certificate requirements',
      items: [],
    },
  };

  // Track which coverages require waiver of subrogation
  const subrogationCoverages = new Set<string>();
  // Track which coverages require additional insured
  const additionalInsuredCoverages = new Set<string>();

  // Process insurance requirements from coverage rules
  const coverageRules =
    data.insuranceRequirements.edges[0]?.node.coverageRules.edges || [];

  for (const { node: rule } of coverageRules) {
    const ruleData = parseRuleJson(rule.ruleJson);
    if (!ruleData) {
      continue;
    }

    const path = rule.ruleName.split('.');
    if (path[0] !== 'coverages') {
      continue;
    }

    const coverageType = path[1];
    const field = path[2];

    // Track additional insured requirements
    if (field === 'addl_insd' && ruleData.condition === 'is_true') {
      switch (coverageType) {
        case 'commercial_general_liability':
          additionalInsuredCoverages.add('general');
          break;
        case 'workers_compensation':
          additionalInsuredCoverages.add('workers compensation');
          break;
        case 'professional_liability':
          additionalInsuredCoverages.add('professional');
          break;
        case 'auto_liability':
          additionalInsuredCoverages.add('auto');
          break;
        case 'excess_umbrella_liability':
          additionalInsuredCoverages.add('umbrella');
          break;
        case 'excess_liability':
          additionalInsuredCoverages.add('excess');
          break;
        case 'cyber_liability':
          additionalInsuredCoverages.add('cyber');
          break;
        case 'crime_liability':
          additionalInsuredCoverages.add('crime');
          break;
        default:
          break;
      }
    }

    // Track waiver of subrogation requirements
    if (field === 'subr_wvd' && ruleData.condition === 'is_true') {
      switch (coverageType) {
        case 'commercial_general_liability':
          subrogationCoverages.add('general');
          break;
        case 'workers_compensation':
          subrogationCoverages.add('workers compensation');
          break;
        case 'professional_liability':
          subrogationCoverages.add('professional');
          break;
        case 'auto_liability':
          subrogationCoverages.add('auto');
          break;
        case 'excess_umbrella_liability':
          subrogationCoverages.add('excess umbrella');
          break;
        case 'excess_liability':
          subrogationCoverages.add('excess');
          break;
        case 'cyber_liability':
          subrogationCoverages.add('cyber');
          break;
        case 'crime_liability':
          subrogationCoverages.add('crime');
          break;
        default:
      }
    }

    // Process coverage limits
    if (field === 'limits') {
      const limitType = path[3];
      const value = ruleData.value;

      switch (coverageType) {
        case 'commercial_general_liability': {
          if (limitType?.includes('dollars')) {
            insurance.generalLiability.items?.push({
              amount: formatCurrency(value),
              description: limitType.replace(/_/g, ' ').replace(' dollars', ''),
            });
          }
          break;
        }
        case 'auto_liability': {
          if (limitType?.includes('dollars')) {
            insurance.autoLiability.amount = formatCurrency(value);
            insurance.autoLiability.description = limitType
              .replace(/_/g, ' ')
              .replace(' dollars', '');
          }
          break;
        }
        case 'workers_compensation': {
          // Set the default text only if we find a workers compensation rule
          insurance.workersCompensation.description =
            'Limits as established by each U.S. State';

          // Process specific workers compensation limits
          if (limitType?.includes('dollars')) {
            if (!insurance.workersCompensation.items) {
              insurance.workersCompensation.items = [];
            }
            insurance.workersCompensation.items.push({
              amount: formatCurrency(value),
              description: limitType.replace(/_/g, ' ').replace(' dollars', ''),
            });
          }
          break;
        }
        case 'professional_liability': {
          if (limitType?.includes('dollars')) {
            insurance.professionalLiability.amount = formatCurrency(value);
            insurance.professionalLiability.description = limitType
              .replace(/_/g, ' ')
              .replace(' dollars', '');
          }
          break;
        }
        case 'excess_umbrella_liability': {
          if (limitType?.includes('dollars')) {
            insurance.excessUmbrellaLiability?.items?.push({
              amount: formatCurrency(value),
              description: limitType.replace(/_/g, ' ').replace(' dollars', ''),
            });
          }
          break;
        }
        case 'excess_liability': {
          if (limitType?.includes('dollars')) {
            insurance.excessLiability?.items?.push({
              amount: formatCurrency(value),
              description: limitType.replace(/_/g, ' ').replace(' dollars', ''),
            });
          }
          break;
        }
        case 'cyber_liability': {
          if (limitType?.includes('dollars')) {
            insurance.cyberLiability?.items?.push({
              amount: formatCurrency(value),
              description: limitType.replace(/_/g, ' ').replace(' dollars', ''),
            });
          }
          break;
        }
        case 'crime_liability': {
          if (limitType?.includes('dollars')) {
            insurance.crimeLiability?.items?.push({
              amount: formatCurrency(value),
              description: limitType.replace(/_/g, ' ').replace(' dollars', ''),
            });
          }
          break;
        }
        case 'automobile_liability': {
          if (limitType?.includes('dollars')) {
            insurance.automobileLiability?.items?.push({
              amount: formatCurrency(value),
              description: limitType.replace(/_/g, ' ').replace(' dollars', ''),
            });
          }
          break;
        }
        default:
          break;
      }
    }
  }

  // Add waiver of subrogation text to certificate requirements
  if (subrogationCoverages.size > 0) {
    const coverageList = Array.from(subrogationCoverages);
    const liabilityCoverages = coverageList.filter(
      (c) => c !== 'workers compensation'
    );
    const hasWorkersComp = coverageList.includes('workers compensation');

    let text = 'Waiver of subrogation required for ';

    if (liabilityCoverages.length > 0) {
      text += liabilityCoverages.join(', ');
      text += ' liability';
    }

    if (hasWorkersComp) {
      if (liabilityCoverages.length > 0) {
        text += ' and ';
      }
      text += 'workers compensation';
    }

    insurance.certificateRequirements?.items.push(`${text}.`);
  }

  // Add additional insured text to certificate requirements
  if (additionalInsuredCoverages.size > 0) {
    const coverageList = Array.from(additionalInsuredCoverages);
    const liabilityCoverages = coverageList.filter(
      (c) => c !== 'workers compensation'
    );
    const hasWorkersComp = coverageList.includes('workers compensation');

    let text = 'Additional insured endorsement required for ';

    if (liabilityCoverages.length > 0) {
      text += liabilityCoverages.join(', ');
      text += ' liability';
    }

    if (hasWorkersComp) {
      if (liabilityCoverages.length > 0) {
        text += ' and ';
      }
      text += 'workers compensation';
    }

    insurance.certificateRequirements?.items.push(`${text}.`);
  }

  // Add the static text about operations description
  insurance.certificateRequirements?.items.push(
    'Description of operations includes primary and non-contributory with any other available insurance.'
  );

  // Ensure sections are shown even if they only have certificate requirements
  if (
    (subrogationCoverages.has('general') ||
      additionalInsuredCoverages.has('general')) &&
    !insurance.generalLiability.description
  ) {
    insurance.generalLiability.description = 'Coverage required';
  }

  if (
    (subrogationCoverages.has('workers compensation') ||
      additionalInsuredCoverages.has('workers compensation')) &&
    !insurance.workersCompensation.description
  ) {
    insurance.workersCompensation.description = 'Coverage required';
  }

  // Extract additional insureds from coverage rules
  const additionalInsuredsFromRules: Array<{
    name: string;
    address: string;
  }> = [];

  for (const { node: rule } of coverageRules) {
    const ruleData = parseRuleJson(rule.ruleJson);
    if (!ruleData) {
      continue;
    }

    // Check for additional insured names in description of operations
    if (
      rule.ruleName === 'description_of_operations.additional_insured_names'
    ) {
      try {
        const insuredsData = JSON.parse(ruleData.value);
        if (Array.isArray(insuredsData)) {
          for (const insured of insuredsData) {
            if (insured.name) {
              const address = [
                insured.line1,
                insured.line2,
                insured.line3,
                insured.locality,
                insured.region,
                insured.postalcode,
              ]
                .filter(Boolean)
                .join(', ');

              additionalInsuredsFromRules.push({
                name: insured.name,
                address,
              });
            }
          }
        }
      } catch (err) {
        console.error('Error parsing additional insureds from rules:', err);
      }
    }
  }

  // Transform additional insureds
  const additionalInsureds = {
    title: 'Additional Insureds',
    items: [
      ...(data.organization.certificateHolderName &&
      data.organization.certificateHolderAddress
        ? [
            {
              name: data.organization.certificateHolderName,
              address: formatAddress(
                data.organization.certificateHolderAddress
              ),
              isCertificateHolder: true,
            },
          ]
        : []),
      ...data.additionalInsureds.edges.map(({ node: insured }) => ({
        name: insured.name,
        address: formatAddress(insured.address),
      })),
      ...additionalInsuredsFromRules,
    ],
  };

  return {
    company: {
      name: data.organization.name,
      subtitle: 'Insurance requirements',
    },
    header: {
      title: data.name,
      description: data.description,
    },
    insurance,
    additionalInsureds,
    certificateRequirements: insurance.certificateRequirements
      ? {
          title: insurance.certificateRequirements.title,
          items: insurance.certificateRequirements.items || [],
        }
      : {
          title: 'Certificate Requirements',
          items: [],
        },
  };
};
